
import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  Code2, 
  Building2, 
  Users, 
  MessageSquare, 
  TrendingUp,
  ArrowRight,
  Star,
  Sparkles,
  Trophy,
  Target
} from 'lucide-react';
import ProductManagementAssessment from '@/pages/ProductManagementAssessment';
import { ScoreRevealAnimation } from './assessment/ScoreRevealAnimation';
import { ResultsDashboard } from './assessment/ResultsDashboard';

interface LoadedScoreOverviewProps {
  onStart: () => void;
}

const LoadedScoreOverview = ({ onStart }: LoadedScoreOverviewProps) => {
  const [assessmentStage, setAssessmentStage] = useState<'overview' | 'assessment' | 'reveal' | 'results'>('overview');
  const [assessmentResults, setAssessmentResults] = useState<any>(null);

  const dimensions = [
    {
      icon: Code2,
      title: "Technical Capability",
      description: "Domain expertise, methodologies, and execution effectiveness in your field",
      color: "text-accent-blue",
      gradient: "from-accent-blue/20 to-accent-cyan/10"
    },
    {
      icon: Building2,
      title: "Industry Expertise",
      description: "Market dynamics, sector knowledge, and proven track record of industry impact",
      color: "text-accent-green", 
      gradient: "from-accent-green/20 to-accent-cyan/10"
    },
    {
      icon: Users,
      title: "Leadership Impact",
      description: "Influence outcomes, drive initiatives, and shape organizational direction",
      color: "text-accent-orange",
      gradient: "from-accent-orange/20 to-accent-yellow/10"
    },
    {
      icon: MessageSquare,
      title: "Communication Effectiveness",
      description: "Articulate vision, manage stakeholders, and build professional relationships",
      color: "text-accent-pink",
      gradient: "from-accent-pink/20 to-secondary/10"
    },
    {
      icon: TrendingUp,
      title: "Growth Trajectory",
      description: "Learning velocity, adaptation to change, and expansion of capabilities",
      color: "text-primary",
      gradient: "from-primary/20 to-secondary/10"
    }
  ];

  const careerStages = [
    { name: "Explorer", range: "0-35", description: "Building foundations", color: "text-career-explorer", progress: 20 },
    { name: "Achiever", range: "36-65", description: "Developing expertise", color: "text-career-achiever", progress: 40 },
    { name: "Specialist", range: "66-89", description: "Leading with expertise", color: "text-career-specialist", progress: 60 },
    { name: "Authority", range: "90-109", description: "Shaping industry", color: "text-career-authority", progress: 80 },
    { name: "Guru", range: "110-120", description: "Defining the future", color: "text-career-guru", progress: 100 }
  ];

  const handleStartAssessment = () => {
    setAssessmentStage('assessment');
  };

  const handleAssessmentComplete = (answers: any) => {
    // Calculate scores based on answers
    const dimensionScores = [
      {
        name: 'Technical Capability',
        score: Object.entries(answers).filter(([key]) => key.startsWith('tech')).reduce((sum, [, value]) => sum + (value as number), 0) * 4,
        maxScore: 20,
        color: 'text-accent-blue'
      },
      {
        name: 'Industry Expertise', 
        score: Object.entries(answers).filter(([key]) => key.startsWith('ind')).reduce((sum, [, value]) => sum + (value as number), 0) * 4,
        maxScore: 20,
        color: 'text-accent-green'
      },
      {
        name: 'Leadership Impact',
        score: Object.entries(answers).filter(([key]) => key.startsWith('lead')).reduce((sum, [, value]) => sum + (value as number), 0) * 4,
        maxScore: 20,
        color: 'text-accent-orange'
      },
      {
        name: 'Communication Effectiveness',
        score: Object.entries(answers).filter(([key]) => key.startsWith('comm')).reduce((sum, [, value]) => sum + (value as number), 0) * 4,
        maxScore: 20,
        color: 'text-accent-pink'
      },
      {
        name: 'Growth Trajectory',
        score: Object.entries(answers).filter(([key]) => key.startsWith('growth')).reduce((sum, [, value]) => sum + (value as number), 0) * 4,
        maxScore: 20,
        color: 'text-primary'
      }
    ];

    const totalScore = dimensionScores.reduce((sum, dim) => sum + dim.score, 0);
    
    // Get career stage
    const CAREER_STAGES = [
      { name: 'Explorer', range: [0, 35], color: 'text-accent-blue', description: 'Building foundational skills' },
      { name: 'Practitioner', range: [36, 55], color: 'text-accent-green', description: 'Developing expertise' },
      { name: 'Specialist', range: [56, 75], color: 'text-accent-orange', description: 'Mastering the craft' },
      { name: 'Expert', range: [76, 95], color: 'text-accent-pink', description: 'Leading and innovating' },
      { name: 'Guru', range: [96, 120], color: 'text-primary', description: 'Shaping the industry' }
    ];
    
    const careerStage = CAREER_STAGES.find(stage => 
      totalScore >= stage.range[0] && totalScore <= stage.range[1]
    ) || CAREER_STAGES[0];

    setAssessmentResults({
      totalScore,
      dimensionScores,
      careerStage,
      answers
    });
    
    setAssessmentStage('reveal');
  };

  const handleRevealComplete = () => {
    setAssessmentStage('results');
  };

  if (assessmentStage === 'assessment') {
    return <ProductManagementAssessment />;
  }

  if (assessmentStage === 'reveal' && assessmentResults) {
    return (
      <ScoreRevealAnimation
        totalScore={assessmentResults.totalScore}
        careerStage={assessmentResults.careerStage}
        onComplete={handleRevealComplete}
      />
    );
  }

  if (assessmentStage === 'results' && assessmentResults) {
    return (
      <ResultsDashboard
        totalScore={assessmentResults.totalScore}
        dimensionScores={assessmentResults.dimensionScores}
        careerStage={assessmentResults.careerStage}
        answers={assessmentResults.answers}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-purple-950/10 to-background">
      {/* Floating Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-32 h-32 bg-gradient-cosmic rounded-full opacity-10 floating-element"></div>
        <div className="absolute bottom-32 left-1/4 w-24 h-24 bg-gradient-sunset rounded-full opacity-15 floating-element" style={{animationDelay: '2s'}}></div>
      </div>

      <div className="container mx-auto px-4 py-8 space-y-12 relative z-10">
        {/* Hero Section */}
        <div className="text-center space-y-8 max-w-4xl mx-auto">
          <div className="space-y-6">
            <div className="flex justify-center">
              <Sparkles className="w-16 h-16 text-primary animate-pulse" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gradient-cosmic glow-effect">
              The Loaded Score™
            </h1>
            <p className="text-xl md:text-2xl text-text-secondary">
              Your Career's Universal Metric
            </p>
            <p className="text-lg text-text-muted leading-relaxed max-w-3xl mx-auto">
              The Loaded Score™ is a comprehensive measure of your professional capability and potential. 
              Think of it as a FICO score for your talent - clear, credible, and universally understood.
            </p>
          </div>
          
          <Button 
            size="lg" 
            onClick={handleStartAssessment}
            className="bg-gradient-vibrant hover:opacity-90 text-xl px-12 py-8 rounded-full shadow-2xl hover:scale-105 transition-all duration-300 glow-effect"
          >
            <Trophy className="mr-3 w-6 h-6" />
            Start Your Assessment
            <ArrowRight className="ml-3 w-6 h-6" />
          </Button>
        </div>

        {/* Five Dimensions - Enhanced Cards */}
        <div className="space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-text-primary mb-4">Five Core Dimensions</h2>
            <p className="text-text-secondary text-lg">
              Your professional capabilities measured across these critical areas
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dimensions.map((dimension, index) => {
              const IconComponent = dimension.icon;
              return (
                <Card 
                  key={index} 
                  className={`p-6 border-0 shadow-xl bg-gradient-to-br ${dimension.gradient} hover:scale-105 transition-all duration-300 h-full group`}
                >
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-xl bg-white/10 backdrop-blur-sm group-hover:bg-white/20 transition-colors">
                        <IconComponent className={`w-8 h-8 ${dimension.color}`} />
                      </div>
                      <Badge variant="secondary" className="font-medium">
                        Dimension {index + 1}
                      </Badge>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-text-primary mb-2 group-hover:text-white transition-colors">
                        {dimension.title}
                      </h3>
                      <p className="text-text-secondary text-sm leading-relaxed group-hover:text-white/90 transition-colors">
                        {dimension.description}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Career Stage Mapping - Enhanced */}
        <div className="space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-text-primary mb-4">Career Stage Progression</h2>
            <p className="text-text-secondary text-lg">
              From Explorer to Guru - see where you stand and what's next
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {careerStages.map((stage, index) => (
              <Card key={index} className="p-6 border-0 shadow-xl bg-gradient-to-br from-card-bg to-card-hover hover:scale-105 transition-all duration-300 h-full group">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className={`text-lg font-bold ${stage.color} group-hover:text-white transition-colors`}>
                      {stage.name}
                    </h3>
                    <Badge variant="outline" className="text-xs">
                      {stage.range}
                    </Badge>
                  </div>
                  <p className="text-text-secondary text-sm group-hover:text-white/90 transition-colors">
                    {stage.description}
                  </p>
                  <Progress value={stage.progress} className="h-2" />
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Call to Action - Enhanced */}
        <Card className="p-12 text-center border-0 shadow-2xl bg-gradient-to-br from-primary/10 via-secondary/10 to-primary/5 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-vibrant opacity-5"></div>
          <div className="relative z-10 space-y-8 max-w-3xl mx-auto">
            <div className="flex justify-center">
              <Target className="w-16 h-16 text-primary" />
            </div>
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-text-primary">Ready to Discover Your Potential?</h2>
              <p className="text-lg text-text-secondary leading-relaxed">
                Take the enhanced assessment now and unlock personalized insights, 
                career stage mapping, and actionable recommendations to accelerate your professional growth.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={handleStartAssessment}
                className="bg-gradient-primary hover:opacity-90 text-lg px-8 py-6"
              >
                <Star className="mr-2 w-5 h-5" />
                Begin Assessment
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                variant="outline"
                size="lg"
                className="text-lg px-8 py-6"
              >
                Learn More About Scoring
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default LoadedScoreOverview;
